import type { ReactNode } from "react"
import { useState } from "react"
import {
	IconArrowUpRightOutline18 as ArrowUpRight,
	IconContrastOutline18 as Contrast,
	IconCopyOutline18 as CopyIcon,
	IconInputFieldOutline18 as InputField,
	IconMoonOutline18 as Moon,
	IconSunOutline18 as Sun,
	IconWindowBottomOutline18 as WindowBottom,
	IconWindowBottomLeftOutline18 as WindowBottomLeft,
	IconWindowBottomRightOutline18 as WindowBottomRight,
} from "nucleo-ui-outline-18"
import { useCopyToClipboard } from "react-use"

import { cn } from "@productlane/lib/src/browser"
import { Button, OptionCards, Popover, SegmentedControl } from "@productlane/ui"

import type {
	SnippetMode,
	SnippetOptions,
	SnippetPosition,
	WidgetType,
} from "./install-snippet-data"
import { toast } from "@/components/toast"
import {
	nextSnippetOptions,
	SNIPPET_DEFAULTS,
	WIDGET_DOCS_URL,
	widgetTypeOf,
} from "./install-snippet-data"

const WIDGET_TYPES = [
	{
		value: "launcher",
		label: "Launcher",
		Icon: WindowBottomRight,
		description: "A button in the corner that opens the full widget.",
		badge: "Best for web apps",
	},
	{
		value: "spotlight",
		label: "Spotlight",
		Icon: InputField,
		description: "An input at the bottom of the page that opens the AI agent.",
		badge: "Best for websites",
	},
] as const satisfies readonly {
	value: WidgetType
	label: string
	Icon: typeof InputField
	description: string
	badge: string
}[]

const POSITIONS = [
	{ value: "left", label: "Left", Icon: WindowBottomLeft },
	{ value: "center", label: "Center", Icon: WindowBottom },
	{ value: "right", label: "Right", Icon: WindowBottomRight },
] as const satisfies readonly {
	value: SnippetPosition
	label: string
	Icon: typeof InputField
}[]

const MODES = [
	{ value: "auto", label: "Auto", Icon: Contrast },
	{ value: "light", label: "Light", Icon: Sun },
	{ value: "dark", label: "Dark", Icon: Moon },
] as const satisfies readonly {
	value: SnippetMode
	label: string
	Icon: typeof InputField
}[]

function Row({
	title,
	description,
	children,
}: {
	title: string
	description: string
	children: ReactNode
}) {
	return (
		<div className="flex items-center justify-between gap-3">
			<div className="flex min-w-0 flex-col gap-0.5">
				<span className="text-[13px] font-medium text-primary">{title}</span>
				<span className="text-xs leading-snug text-secondary">
					{description}
				</span>
			</div>
			{children}
		</div>
	)
}

export function SnippetCopyPopover({
	code,
	options,
	onChange,
	triggerClassName,
}: {
	code: string
	options: SnippetOptions
	onChange: (options: SnippetOptions) => void
	triggerClassName: string
}) {
	const [open, setOpen] = useState(false)
	const [, copy] = useCopyToClipboard()
	const update = (patch: Partial<SnippetOptions>) =>
		onChange(nextSnippetOptions(options, patch))

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
			align="end"
			sideOffset={6}
			triggerTooltip="Copy snippet"
			className="w-[22rem] p-3"
			content={
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-2">
						<span className="text-[13px] font-medium text-primary">
							Which widget do you want to install?
						</span>
						<OptionCards
							name="widget-type"
							value={widgetTypeOf(options)}
							options={WIDGET_TYPES}
							onValueChange={(type) =>
								update({ spotlight: type === "spotlight" })
							}
							className="grid-cols-2 sm:grid-cols-2"
						/>
					</div>
					<Row title="Position" description="Where the widget sits.">
						<SegmentedControl
							name="widget-position"
							value={options.position ?? SNIPPET_DEFAULTS.position}
							options={POSITIONS}
							onValueChange={(position) => update({ position })}
						/>
					</Row>
					<Row
						title="Color scheme"
						description="Auto follows the visitor's system."
					>
						<SegmentedControl
							name="widget-mode"
							value={options.mode ?? SNIPPET_DEFAULTS.mode}
							options={MODES}
							onValueChange={(mode) => update({ mode })}
						/>
					</Row>
					<div className="flex items-center justify-between gap-2">
						<Button
							variant="link"
							Icon={ArrowUpRight}
							onClick={() => window.open(WIDGET_DOCS_URL, "_blank", "noopener")}
						>
							More options
						</Button>
						<Button
							Icon={CopyIcon}
							onClick={() => {
								copy(code)
								setOpen(false)
								toast.success({ title: "Copied snippet to clipboard" })
							}}
						>
							Copy snippet
						</Button>
					</div>
				</div>
			}
		>
			<button
				type="button"
				aria-label="Copy snippet"
				className={cn(triggerClassName, "w-6 justify-center px-0")}
			>
				<CopyIcon className="h-3.5 w-3.5 flex-none" />
			</button>
		</Popover>
	)
}
